import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PrinterIcon } from "@heroicons/react/outline";
import { ComponentType, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { CellProps } from "react-table";

import {
  AdminLabOrderList,
  CaseAdmin,
  CaseDocumentAdmin,
  LabOrder,
  SlideAdmin,
} from "pwapi";

import { printLabOrdersSlides } from "shared/actions/admin/adminCaseActions";
import { printSlideAdminLabOrder } from "shared/actions/labOrdersActions";
import { useIsLoadingAny } from "shared/hooks/requestStatusHooks";
import {
  ExternalLabsState,
  ExternalLabStainsState,
} from "shared/reducers/frontLoaderReducers";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { timestampToLocaleDateTimeString } from "shared/utils/dates";
import { getDocumentLabel } from "shared/utils/documentUtils";
import { splitFlagReason } from "shared/utils/labOrderUtils";
import { appendSendoutIfNeeded } from "shared/utils/labUtils";
import {
  printSendoutRequisitionLink,
  printSlidesLink,
} from "shared/utils/printUtils";
import { getStainByLabStainId } from "shared/utils/stainUtils";
import { getNameString } from "shared/utils/userUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import DjangoLink from "components/DjangoLink";
import { useAppDispatch, useAppSelector } from "store/hooks";

import CopyBlockButton from "./CopyBlockButton";
import OrderCancellationButton from "./OrderCancellationButton";
import OrderCompletionButton from "./OrderCompletionButton";
import OrderIcon from "../../OrderIcon";
import { SlideFlagIcon } from "../../viewer/SlideFlagIcon";
import {
  getOrderStatus,
  mapOrderStatusToLabel,
} from "../editCasePanel/Orders.helpers";

export const withOrderStatusRowProp = (hooks: any) => {
  hooks.getRowProps.push((props: any, { row }: any) => {
    // row.original already contains your derived status
    // const status = row?.original?.status; // "completed" | "cancelled" | "in-progress"
    const order = row?.original;
    const status = getOrderStatus(order);

    return {
      ...props,
      ...(status ? { "data-order-status": status } : null),
    };
  });
};
export const withCellDataAttrs = (hooks: any) => {
  hooks.getCellProps.push((props: any, { cell }: any) => {
    const col = cell?.column?.id; // e.g., "status", "created_at"
    return {
      ...props,
      ...(col ? { "data-col": col } : null),
    };
  });
};

const OrdersColumns =
  <P extends object>(
    Component: ComponentType<P>,
    options?: {
      hasFlagReason?: boolean;
      canOpenCase?: boolean;
      canCancel?: boolean;
      canCompleteOrder?: boolean;
      displayClipboardButton?: boolean;
      printSlideFromOrderButton?: boolean;
    }
  ) =>
  (props: any) => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const labStains = useAppSelector(({ labStains }) => labStains);
    const stains = useAppSelector(({ stains }) => stains);

    const cancelSentOrderFlag = useAppSelector((state) =>
      selectFeatureFlag(state, "canCancelSentLabOrder")
    );
    const cancelAutomatedOrderFlag = useAppSelector((state) =>
      selectFeatureFlag(state, "canCancelAutomatedOrder")
    );
    const completeOrderFlag = useAppSelector((state) =>
      selectFeatureFlag(state, "canCompleteLabOrder")
    );
    const canPrintLabOrders = useAppSelector((state) =>
      selectFeatureFlag(state, "canPrintLabOrders")
    );
    const printSlideFromOrder = useAppSelector((state) =>
      selectFeatureFlag(state, "printSlideFromOrder")
    );

    const externalLabs = useAppSelector(
      ({ externalLabs }: { externalLabs: ExternalLabsState }) => externalLabs
    );
    const externalLabStains = useAppSelector(
      ({ externalLabStains }: { externalLabStains: ExternalLabStainsState }) =>
        externalLabStains
    );

    const getLabStainMeta = useCallback(
      (labStainId) => {
        const labStain = labStains[labStainId || 0];

        const externalLabStain =
          externalLabStains[labStain?.external_lab_stain_id || 0]
            ?.external_lab_id || 0;

        const externalLab = externalLabs[externalLabStain];

        const externalLabName = externalLab?.name
          ? appendSendoutIfNeeded(externalLab.name)
          : externalLab?.name;

        return {
          labStain,
          isExternalLabStain: !!externalLabStain,
          externalLabName,
        };
      },

      [labStains, externalLabStains]
    );

    const loadingPrintSlideAdminLabOrder = useIsLoadingAny(
      printSlideAdminLabOrder
    );
    const loadingPrintLabOrdersSlides = useIsLoadingAny(printLabOrdersSlides);

    const columns = useMemo(
      () =>
        [
          {
            Header: "Status",
            accessor: "status",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              const orderStatus = getOrderStatus(row.original);
              return (
                <>
                  <OrderIcon status={orderStatus} />{" "}
                  {mapOrderStatusToLabel[orderStatus]}
                </>
              );
            },
          },

          ...(options?.canOpenCase
            ? [
                {
                  Header: "Case Priority",
                  accessor: "case_priority",
                  Cell: ({ row }: CellProps<AdminLabOrderList>) => {
                    const { case_priority: priority } = row.original;
                    return priority &&
                      priority !== CaseAdmin.priority.NORMAL ? (
                      <div
                        className={`AdminCaselist__priority AdminCaselist__priority--${priority}`}
                      >
                        {priority}
                      </div>
                    ) : row.original.clinic_tat ? (
                      <div
                        className={`AdminCaselist__priority AdminCaselist__priority--tat`}
                      >
                        Priority
                      </div>
                    ) : null;
                  },
                },
                {
                  Header: "Case Accession",
                  accessor: "case__accession",
                  Cell: ({ row }: CellProps<AdminLabOrderList>) => (
                    <>{row.original.case_accession}</>
                  ),
                },
              ]
            : []),
          {
            Header: "Type",
            accessor: "type",
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              const stain = getStainByLabStainId(
                row.original.lab_stain_id || 0,
                stains,
                labStains
              );

              return (
                <div className="PWTable__flex--column">
                  {stain?.deliverable_document_category ? (
                    <span className="PWTable__documentCategory">
                      <FontAwesomeIcon
                        icon="file"
                        className="PWTable__documentCategory--margin"
                      />
                      {getDocumentLabel(
                        stain.deliverable_document_category as CaseDocumentAdmin.category
                      )}
                    </span>
                  ) : (
                    <span>{row.original.type}</span>
                  )}
                </div>
              );
            },
          },
          {
            accessor: "is_sendout",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              const { lab_stain_id } = row.original;
              const { isExternalLabStain } = getLabStainMeta(lab_stain_id);

              return (
                isExternalLabStain && (
                  <span className="icon-wrap">
                    <FontAwesomeIcon
                      className="orders__sendout--icon"
                      icon="truck"
                      title="Sendout"
                    />
                  </span>
                )
              );
            },
          },

          {
            Header: "Ordered Stains",
            accessor: "stain_name",
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              const stain = getStainByLabStainId(
                row.original.lab_stain_id || 0,
                stains,
                labStains
              );
              if (!stain) return null;

              const { lab_stain_id } = row.original;
              const { labStain, externalLabName } =
                getLabStainMeta(lab_stain_id);

              return (
                <span
                  className={
                    row.original.cancelled_at ? "orders__cancelled" : ""
                  }
                >
                  {externalLabName && (
                    <span className={"orders__sendout--lab"}>
                      {externalLabName}
                    </span>
                  )}
                  <span>{row.original.block_name}</span>{" "}
                  <strong>{stain.name}</strong>{" "}
                  <span style={{ color: "var(--steel)" }}>
                    {stain.category}
                  </span>
                  {labStain?.label_override && <>: {labStain.label_override}</>}
                </span>
              );
            },
          },
          {
            Header: "Ordering User",
            accessor: "user",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => (
              <span
                className={row.original.cancelled_at ? "orders__cancelled" : ""}
              >
                {getNameString(
                  row.original.user?.first_name,
                  row.original.user?.last_name,
                  true
                )}
              </span>
            ),
          },
          ...(options?.canOpenCase
            ? [
                {
                  Header: "Patient",
                  accessor: "patient",
                  disableSortBy: true,
                  Cell: ({ row }: CellProps<AdminLabOrderList>) => (
                    <>
                      {getNameString(
                        row.original.patient?.first_name,
                        row.original.patient?.last_name,
                        true
                      )}
                    </>
                  ),
                },
              ]
            : []),
          {
            Header: "Order Placed At",
            accessor: "created_at",
            Cell: ({ row }: CellProps<AdminLabOrderList>) => (
              <span
                className={row.original.cancelled_at ? "orders__cancelled" : ""}
              >
                {timestampToLocaleDateTimeString(row.original.created_at || "")}
              </span>
            ),
          },
          {
            Header: "Completed At",
            accessor: "completed_at",
            diableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) =>
              row.original.completed_at ? (
                <>
                  {timestampToLocaleDateTimeString(
                    row.original.completed_at || ""
                  )}
                </>
              ) : (
                <></>
              ),
          },
          ...(canPrintLabOrders && options?.canOpenCase
            ? [
                {
                  Header: "Printed At",
                  accessor: "printed_at",
                  disableSortBy: true,
                  Cell: ({ row }: CellProps<AdminLabOrderList>) => {
                    const printer = getNameString(
                      //@ts-ignore
                      row.original.printed_by?.first_name,
                      //@ts-ignore
                      row.original.printed_by?.last_name,
                      true
                    );
                    return row.original.printed_at ? (
                      <span>
                        {timestampToLocaleDateTimeString(
                          row.original.printed_at || ""
                        )}
                        {printer && (
                          <>
                            <br />
                            {printer}
                          </>
                        )}
                      </span>
                    ) : (
                      <></>
                    );
                  },
                },
              ]
            : []),
          {
            Header: "Note",
            accessor: "note",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              return (
                <div
                  style={{ maxWidth: "400px" }}
                  className={
                    row.original.cancelled_at ? "orders__cancelled" : ""
                  }
                >
                  {options?.hasFlagReason && row.original.flag?.reason && (
                    <>
                      <SlideFlagIcon />
                      <span style={{ color: "var(--barnRed)" }}>
                        {" "}
                        {splitFlagReason(row.original.flag.reason)}
                      </span>
                      <br />
                    </>
                  )}
                  {row.original.note}
                </div>
              );
            },
          },
          {
            Header: "Actions",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              const labStain = labStains[row.original.lab_stain_id || 0];
              const externalLab =
                externalLabs[
                  externalLabStains[labStain?.external_lab_stain_id || 0]
                    ?.external_lab_id || 0
                ];

              return (
                <div
                  className="cell__content--horizontal cell__content--actions"
                  style={{ gap: "0.5em" }}
                >
                  {externalLab && row.original.case_id && (
                    <Link
                      to={printSendoutRequisitionLink(row.original.case_id)}
                      target="_blank"
                    >
                      <PathologyWatchButton size="small">
                        <>
                          <PrinterIcon height={20} color="var(--steel)" />
                          &nbsp; Sendout Requisition
                        </>
                      </PathologyWatchButton>
                    </Link>
                  )}
                  {options?.displayClipboardButton && row.original.block_id && (
                    <CopyBlockButton block_id={row.original.block_id} />
                  )}
                  {options?.canOpenCase && (
                    <Link
                      to={`/admin/cases/${row.original.case_id}`}
                      state={{
                        label: "Orders",
                        prevUrl: `${location.pathname}${location.search}`,
                      }}
                    >
                      <PathologyWatchButton size="small">
                        Edit Case
                      </PathologyWatchButton>
                    </Link>
                  )}
                  {options?.printSlideFromOrderButton &&
                    printSlideFromOrder &&
                    props.ordersWithoutSlides
                      ?.map((o: LabOrder) => o.id)
                      .includes(row.original.id) &&
                    !row.original.completed_at &&
                    !row.original.cancelled_at && (
                      <PathologyWatchButton
                        onClick={() => {
                          dispatch(
                            printSlideAdminLabOrder({ id: row.original.id! })
                          ).then(
                            (r: SlideAdmin) =>
                              r.case?.id &&
                              r.id &&
                              window.open(
                                printSlidesLink(r.case.id, r, "zebra")
                              )
                          );
                        }}
                        disabled={
                          loadingPrintSlideAdminLabOrder ||
                          loadingPrintLabOrdersSlides
                        }
                      >
                        Create Slide for Order
                      </PathologyWatchButton>
                    )}
                  {options?.canCompleteOrder &&
                    completeOrderFlag &&
                    !row.original.completed_at &&
                    !row.original.cancelled_at && (
                      <OrderCompletionButton order={row.original as LabOrder} />
                    )}
                  {options?.canCancel &&
                    ((row.original.user?.id && cancelSentOrderFlag) ||
                      (!row.original.user?.id && cancelAutomatedOrderFlag)) &&
                    !row.original.completed_at &&
                    !row.original.cancelled_at && (
                      <OrderCancellationButton
                        order={row.original as LabOrder}
                      />
                    )}
                  {row.original.slide_id && (
                    <Link
                      to={`/cases/${row.original.case_id}/slides/${row.original.slide_id}`}
                      title="View Slide"
                      target="_blank"
                    >
                      <PathologyWatchButton size="small">
                        <>
                          <SlideFlagIcon />
                          &nbsp;&rarr;
                        </>
                      </PathologyWatchButton>
                    </Link>
                  )}
                  {row.original.id && (
                    <DjangoLink
                      route={`lab/laborder/${row.original.id}/change/`}
                    />
                  )}
                </div>
              );
            },
          },
        ] as any,
      [
        location,
        loadingPrintSlideAdminLabOrder,
        printSlideFromOrder,
        props.ordersWithoutSlides,
      ]
    );

    return <Component columns={columns} {...props} />;
  };

export default OrdersColumns;
